import { invoicePayments } from '@markku/db'
import { createInsertSchema, createSelectSchema } from 'drizzle-zod'
import { z } from 'zod'

// Base schemas from DB
export const selectInvoicePaymentSchema = createSelectSchema(invoicePayments)
export const insertInvoicePaymentSchema = createInsertSchema(
  invoicePayments,
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
  importedAt: true,
})

export type InvoicePayment = z.infer<typeof selectInvoicePaymentSchema>
export type CreateInvoicePayment = z.infer<typeof insertInvoicePaymentSchema>
export type InvoiceSummary = z.infer<typeof invoiceSummarySchema>

// Parse result - what we return after parsing a KTL file
export const paymentFileTransactionSchema = z.object({
  referenceNumber: z.string(),
  amountCents: z.number(),
  paymentDate: z.string(), // ISO date
  archiveId: z.string(),
  payerName: z.string(),
  status: z.enum(['matched', 'already_recorded', 'unmatched']),
  invoice: z
    .object({
      id: z.number(),
      invoiceNumber: z.string(),
      stripeInvoiceId: z.string(),
      totalAmountCents: z.number(),
      paidAmountCents: z.number(), // Sum of existing payments
      status: z.string(),
      dueDate: z.string().nullable(),
      customerId: z.number().nullable(),
      customerName: z.string().nullable(),
    })
    .nullable(),
  willComplete: z.boolean(), // True if this payment will make total >= invoice amount
})

export type PaymentFileTransaction = z.infer<
  typeof paymentFileTransactionSchema
>

export const parsePaymentFileResultSchema = z.object({
  transactions: z.array(paymentFileTransactionSchema),
  fileDate: z.string(),
  summary: z.object({
    total: z.number(),
    matched: z.number(),
    alreadyRecorded: z.number(),
    unmatched: z.number(),
  }),
})

export type ParsePaymentFileResult = z.infer<
  typeof parsePaymentFileResultSchema
>

// Shared shape for invoice summaries in import results
export const invoiceSummarySchema = z.object({
  invoiceId: z.number(),
  invoiceNumber: z.string(),
  stripeInvoiceId: z.string(),
  totalPaid: z.number(), // cents
  invoiceTotal: z.number(), // cents
  customerId: z.number().nullable(),
  customerName: z.string().nullable(),
})

// Import result - what we return after importing payments
export const paymentImportResultSchema = z.object({
  inserted: z.number(),
  skipped: z.number(), // Duplicates (archive_id already exists)
  invoicesPaid: z.array(invoiceSummarySchema),
  invoicesPartial: z.array(
    invoiceSummarySchema.extend({ isOverdue: z.boolean() }),
  ),
  invoicesOverpaid: z.array(invoiceSummarySchema),
  invoicesAlreadyPaid: z.array(invoiceSummarySchema),
  invoicesVoided: z.array(
    z.object({
      invoiceId: z.number(),
      invoiceNumber: z.string(),
    }),
  ),
  errors: z.array(
    z.object({
      invoiceId: z.number().optional(),
      invoiceNumber: z.string().optional(),
      archiveId: z.string(),
      message: z.string(),
    }),
  ),
})

export type PaymentImportResult = z.infer<typeof paymentImportResultSchema>

// Input for the import endpoint
export const importPaymentInputSchema = z.object({
  invoiceId: z.number(),
  amountCents: z.number(),
  paymentDate: z.string(), // ISO date
  archiveId: z.string(),
  referenceNumber: z.string(),
  payerName: z.string().nullable(),
})

export type ImportPaymentInput = z.infer<typeof importPaymentInputSchema>
